import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback
} from "react";
import { Container, Col, Row } from "shards-react";
import "./SubAdminDetails.css";
import { useParams } from "react-router-dom";
import { db } from "../../../firebase";
import {
  collection,
  doc,
  documentId,
  getDoc,
  onSnapshot,
  query,
  where
} from "firebase/firestore";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import Charts from "react-apexcharts";
import StudentTestsChart from "./StudentTestsChart";
import { UsersContext } from "../../../providers/Users";

function SubAdminStudentsTrainingTracker() {
  const { id, name } = useParams();
  const {
    assignedUserIds,
    getSubAdminData,
    assignedUsers,
    setAssignedUsers
  } = useContext(UsersContext);
  const [loading, setLoading] = useState(false);
  const [sessionsDoneLog, setSessionsDoneLog] = useState([]);
  const [scheduledTests, setScheduledTests] = useState([]);
  const [doneTests, setDoneTests] = useState([]);
  const moment = require("moment");
  const chartRef = useRef(null);
  const [heatMapSeries, setHeatMapSeries] = useState([]);
  const [chartheight, setChartHeight] = useState(400);

  const options = {
    noData: {
      text: "Loading...",
      style: {
        fontSize: "18px"
      }
    },
    chart: {
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 0,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: [
            {
              from: -10,
              to: -5,
              name: "",
              color: "#FFFFFF"
            },
            {
              from: 1.1,
              to: 2,
              name: "Trained",
              color: "#009e1a" //green
            },
            {
              from: 3,
              to: 4,
              name: "None 2 days",
              color: "#ffd454" //yallow
            },
            {
              from: 4.1,
              to: 6,
              name: "None 3 days",
              color: "#fd7e14" //orange
            },
            {
              from: 14,
              to: 20,
              name: "None >3 days",
              color: "#fd0000" //red
            },
            {
              from: -2,
              to: 0,
              name: "No training",
              color: "#b4b5b6" //gray
            },
            {
              from: 11,
              to: 13,
              name: "Next test",
              color: "#0e7be0"
            },
            {
              from: 21,
              to: 60,
              name: "Need attention",
              color: "#000000"
            },
            {
              from: 61,
              to: 65,
              name: "Tomorrow",
              color: "#6c757d"
            }
          ]
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      width: 1
    },
    title: {},
    tooltip: {
      enabled: true,
      enabledOnSeries: undefined,
      y: {
        formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
          return "";
        },
        title: {
          formatter: seriesName => seriesName
        }
      },
      x: {
        formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
          return moment(value).format("DD-MMM-YY");
        }
      }
    },
    xaxis: {
      type: "datetime",
      tickAmount: 12,
      labels: {
        hideOverlappingLabels: true,
        showDuplicates: false,
        formatter: function(val, timestamp, opts) {
          const weekNumber = moment(val).isoWeek();
          return `w${weekNumber}`;
        }
      }
    },
    yaxis: {
      labels: {
        formatter: function(value, index) {
          return value;
        }
      }
    }
  };

  useEffect(() => {
    setChartHeight(assignedUsers.length * 20 + 80);
  }, [assignedUsers]);

  useEffect(() => {
    getSubAdminData(id);
  }, [id]);

  useEffect(() => {
    const fetchScheduledTestsForUser = async () => {
      try {
        assignedUserIds.map(async userDoc => {
          // Construct the URL for fetching scheduled tests data based on the userId and optional start/end dates.
          let url = `${process.env.REACT_APP_API_GATEWAY_URL}users/${userDoc.id}/scheduled-tests`;
          const response = await fetch(url);
          const fetchedData = await response.json();
          if (fetchedData.length > 0) {
            const hasNoCompletionDate = fetchedData.filter(
              item => item.completion_datetime === null
            );
            hasNoCompletionDate.sort((a, b) =>
              moment(a.scheduled_datetime).diff(moment(b.scheduled_datetime))
            );
            const sTests = hasNoCompletionDate.map((test, indx) => {
              return {
                nextTest: moment(test.scheduled_datetime).format("DD-MMM-YY"),
                userId: test.User.uuid,
                testName: test.Test.name
              };
            });
            const hasCompletionDate = fetchedData.filter(
              item => item.completion_datetime !== null
            );
            hasCompletionDate.sort((a, b) =>
              moment(a.completion_datetime).diff(moment(b.completion_datetime))
            );
            const dTests = hasCompletionDate.map((test, indx) => {
              return {
                id: test.id,
                testDoneAt: moment(test.completion_datetime).format(
                  "DD-MMM-YY"
                ),
                userId: test.User.uuid
              };
            });
            setDoneTests(prev => [...prev, dTests]);
            setScheduledTests(prev => [...prev, sTests]);
          }
        });
      } catch (error) {
        console.error("Error:", error);
        return []; // Return an empty array in case of error
      }
    };
    fetchScheduledTestsForUser();
  }, [assignedUserIds]);

  useEffect(() => {
    let usersData = [];
    const collRef = collection(db, "UserData");
    let unsubscribe;
    setAssignedUsers([]);
    if (assignedUserIds.length > 0) {
      let ids = [];
      for (let i = 0; i < assignedUserIds.length; i++) {
        if (ids.length <= 10) {
          ids.push(assignedUserIds[i]);
        }
        if (ids.length === 10 || i === assignedUserIds.length - 1) {
          const queryUsers = query(collRef, where(documentId(), "in", ids));
          unsubscribe = onSnapshot(queryUsers, data => {
            data.docChanges().forEach(async change => {
              if (change.type === "added") {
                usersData.push({ uid: change.doc.id, ...change.doc.data() });
              }
              const sortedUsersData = usersData.sort((a, b) => {
                return a.FirstName.localeCompare(b.FirstName);
              });
              setAssignedUsers([...sortedUsersData]);
            });
          });
          ids = [];
        }
      }
    }
    return unsubscribe;
  }, [assignedUserIds]);

  useEffect(() => {
    getUserPrograms(assignedUsers);
  }, [assignedUsers]);

  useEffect(() => {
    let trainingData = [];
    const sortedArr = sortArrayByString(sessionsDoneLog);
    const mounthsDays = getLastTrainingDates(sortedArr);
    const formatedCurrentDate = moment().format("DD-MMM-YY");
    let nextDay = moment()
      .add(1, "days")
      .format("DD-MMM-YY");
    if (sessionsDoneLog.length > 0) {
      trainingData = sortedArr.map(session => {
        let userData = [];
        let uniqueSessionsDates = [...new Set(session.sessionsDates)];
        const sortedDates = uniqueSessionsDates
          .map(date => moment(date, "DD-MMM-YY"))
          .sort((a, b) => a - b);
        const formattedDates = sortedDates.map(date =>
          date.format("DD-MMM-YY")
        );
        mounthsDays.forEach((date, indx, arr) => {
          let dateIsExist =
            userData.find(el => {
              return el.x === date;
            }) !== undefined;
          formattedDates.forEach((sesDate, idx, sesDateArr) => {
            if (date === sesDate) {
              const yVal = Math.round(
                daysDifference(sesDateArr[idx], sesDateArr[idx + 1])
              );
              if (yVal === 2) {
                userData.push(
                  { x: arr[indx], y: 2 },
                  { x: arr[indx + 1], y: 0 }
                );
              } else if (yVal === 3) {
                userData.push(
                  { x: arr[indx], y: 2 },
                  { x: arr[indx + 1], y: 0 },
                  { x: arr[indx + 2], y: 4 }
                );
              } else if (yVal === 4) {
                userData.push(
                  { x: arr[indx], y: 2 },
                  { x: arr[indx + 1], y: 0 },
                  { x: arr[indx + 2], y: 4 },
                  { x: arr[indx + 3], y: 6 }
                );
              } else if (yVal > 4) {
                userData.push(
                  { x: arr[indx], y: 2 },
                  { x: arr[indx + 1], y: 0 },
                  { x: arr[indx + 2], y: 4 },
                  { x: arr[indx + 3], y: 6 }
                );
                for (let w = 0; w < yVal - 4; w++) {
                  userData.push({ x: arr[indx + w + 4], y: 20 });
                }
              } else {
                uniqueSessionsDates.includes(date) &&
                  !dateIsExist &&
                  userData.push({ x: arr[indx], y: 2 });
              }
            }
          });
          !uniqueSessionsDates.includes(date) &&
            !dateIsExist &&
            userData.push({ x: arr[indx], y: 0 });
          const uniqueSchaduleTests = uniqueTestsData(scheduledTests);
          uniqueSchaduleTests.forEach((element, index) => {
            if (
              date === element.nextTest &&
              session.userId === element.userId
            ) {
              for (let z = 4; z >= 0; z--) {
                if (moment(arr[indx - z]).isAfter()) {
                  const now = moment();
                  let duration = moment.duration(
                    moment(element.nextTest).diff(now)
                  );
                  let diffDate = Math.ceil(duration.asDays());
                  if (diffDate < 5) {
                    !uniqueSessionsDates.includes(element.nextTest) &&
                      userData.splice(indx, 1, { x: arr[indx], y: 13 });
                  } else {
                    !uniqueSessionsDates.includes(element.nextTest) &&
                      userData.splice(indx - z, z, { x: arr[indx - z], y: 13 });
                  }
                }
              }
            }
          });
        });

        const currentIndex = userData.findIndex(
          obj => obj.x === formatedCurrentDate
        );
        if (currentIndex !== -1) {
          const backwardData = userData.slice(
            currentIndex - 13,
            currentIndex + 1
          );
          const hasZerosInRow = backwardData.reduce(
            (acc, obj) => {
              if (obj.y === 0) {
                acc.count++;
                if (acc.count === 5) {
                  acc.result = true;
                }
              } else {
                acc.count = 0;
              }
              return acc;
            },
            { count: 0, result: false }
          ).result;
          const sumY = backwardData.reduce((acc, obj) => {
            if (obj.y === 4 || obj.y === 6 || obj.y === 20) acc += obj.y;
            return acc;
          }, 0);
          if (sumY > 34 || hasZerosInRow) {
            userData.splice(0, 0, { x: "Need attention", y: 60 });
          } else {
            userData.splice(0, 0, { x: "", y: -5 });
          }
        }
        userData.splice(
          userData.findIndex(item => item.x === nextDay),
          1,
          { x: nextDay, y: 65 }
        );
        return {
          name: session.userName,
          data: userData
        };
      });
    }
    setHeatMapSeries(trainingData);
  }, [sessionsDoneLog, assignedUsers, scheduledTests]);

  const sortArrayByString = arr => {
    arr.sort((a, b) => {
      const nameA = a.userName.toUpperCase(); // ignore upper and lowercase
      const nameB = b.userName.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return 1;
      }
      if (nameA > nameB) {
        return -1;
      }
      return 0;
    });
    return arr;
  };

  const getLastTrainingDates = data => {
    const currentDate = moment();
    const startingDate = currentDate.clone().subtract(77, "days");
    const endingDate = currentDate.clone().add(7, "days");
    const formattedDates = [];
    let currentDatePointer = startingDate.clone();
    while (currentDatePointer.isSameOrBefore(endingDate)) {
      formattedDates.push(currentDatePointer.format("DD-MMM-YY"));
      currentDatePointer.add(1, "day");
    }
    const allSessionDates = data.flatMap(user => user.sessionsDates);
    const momentSessionDates = allSessionDates.map(dateStr =>
      moment(dateStr, "DD-MMM-YY")
    );
    const latestDate = moment.max(momentSessionDates);
    const earliestDate = moment.min(momentSessionDates);
    const daysDifference = latestDate.diff(earliestDate, "days");
    let calenderDays = [];
    if (daysDifference > 84) {
      calenderDays = [...formattedDates];
    } else {
      for (let i = 0; i < 84; i++) {
        const date = earliestDate.clone().add(i, "days");
        calenderDays.push(date.format("DD-MMM-YY"));
      }
    }
    return calenderDays;
  };

  const uniqueTestsData = tests => {
    const filterdtests = tests
      .flat()
      .filter(
        (test, index, self) =>
          index ===
          self.findIndex(
            t => t.userId === test.userId && t.nextTest === test.nextTest
          )
      );
    return filterdtests;
  };

  const daysDifference = (date1, date2) => {
    let diffDays = 0;
    if (date1 !== undefined && date2 !== undefined) {
      const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
      const firstDate = new Date(date1);
      const secondDate = new Date(date2);
      diffDays = Math.abs((firstDate - secondDate) / oneDay);
    }
    return diffDays;
  };

  const getUserPrograms = useCallback(
    async assignedUsers => {
      let data = [];
      let userName = "";
      let userId = "";
      if (assignedUsers) {
        const trainigSessions = await Promise.all(
          assignedUsers.map(async user => {
            const docRef = doc(db, "UserProgram", user.Programs.id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              data = docSnap.data().SessionsDoneLog
                ? docSnap.data().SessionsDoneLog
                : [];
              userName = user.FirstName;
              userId = user.uid;
              return { data, userName, userId };
            } else {
              console.log("No such document!");
            }
          })
        );
        const userSessionsFormatedDates = formatStudentSessionDates(trainigSessions);
        let arrOfSessions = [];
        if (userSessionsFormatedDates.length > 0) {
          userSessionsFormatedDates.forEach((item, index) => {
            arrOfSessions.push({
              userId: item.userId,
              userName: item.userName,
              sessionsDates: item.sessionsDates
            });
          });
          setSessionsDoneLog(arrOfSessions.reverse());
        }
      }
    },
    [assignedUsers]
  );

  const formatStudentSessionDates = useCallback(sessions => {
    let sessionsArr = [];
    sessions.forEach((item, index) => {
      const sessionsArr2 = item.data.map(ses => {
        return moment(ses.SessionStartedAt.toDate().toString()).format(
          "DD-MMM-YY"
        );
      });
      sessionsArr[index] = {
        sessionsDates: sessionsArr2,
        userName: item.userName,
        userId: item.userId
      };
    });
    return sessionsArr;
  }, []);

  return (
    <Container fluid className="main-content-container px-4 py-5">
      {loading ? (
        <div className="wrapper">
          <div className="spinner">
            <div className="spinner-1"></div>
            <div className="spinner-2"></div>
            <div className="spinner-3"></div>
          </div>
        </div>
      ) : (
        <div>
          <h4 className="pb-5">Students training tracker / Teacher: {name}</h4>
          <Col>
            <Charts
              ref={chartRef}
              options={options}
              series={heatMapSeries}
              type="heatmap"
              height={chartheight}
              width={"100%"}
            />
          </Col>
          <Row>
            {assignedUsers.map((user, indx) => {
              return (
                <StudentTestsChart
                  key={user.uid}
                  user={user}
                  scheduledTests={scheduledTests}
                />
              );
            })}
          </Row>
        </div>
      )}
    </Container>
  );
}

export default SubAdminStudentsTrainingTracker;
